import { IAttribute, IModel, IViolation } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Activity } from './activity';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { ModelConstants } from '../model-constants';
import { ParameterValue } from '../base/parameter-value';
import { MicrosoftTeamsLinkedService } from '../linkedservice/microsoft-teams.linkedservice';
import { format } from '../../shared/utils/string.util';
import { LinkedService } from '../linkedservice/linkedservice';
import { Copier } from '../base/copier';
import { GlobalVariables } from '../../constants/globals';

export enum PostInType { SendMessageInChannel, SendMessageInGroupChat }

@Metamodel.model({
    name: ModelConstants.microsoftTeams,
    referable: true
})
export class MicrosoftTeamsActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly CONTENT: IAttribute;
    public static readonly SUBJECT: IAttribute;
    public static readonly CHATID: IAttribute;
    public static readonly TEAMID: IAttribute;
    public static readonly CHANNELID: IAttribute;
    public static readonly OPERATIONTYPE: IAttribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;

    @Metamodel.attribute({
        display: shellStringResources.ConnectionLabel,
        reference: true,
        type: MicrosoftTeamsLinkedService.MODEL,
        constraint: {
            validator: MicrosoftTeamsActivity.validateConnection
        }
    })
    public linkedService: MicrosoftTeamsLinkedService;

    @Metamodel.attribute({
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters?: ParameterValue[] = [];

    @Metamodel.attribute({
        display: shellStringResources.Team,
        association: 'TEAMID',
        templatable: true,
        constraint: {
            validator: MicrosoftTeamsActivity.validateTeamId
        }
    })
    public teamId?: string;

    @Metamodel.attribute({
        display: shellStringResources.Channel,
        association: 'CHANNELID',
        templatable: true,
        constraint: {
            validator: MicrosoftTeamsActivity.validateChannelId
        }
    })
    public channelId?: string;

    @Metamodel.attribute({
        display: shellStringResources.GroupChat,
        association: 'CHATID',
        templatable: true,
        constraint: {
            validator: MicrosoftTeamsActivity.validateChatId
        }
    })
    public chatId?: string;

    @Metamodel.attribute({
        display: shellStringResources.Message,
        association: 'CONTENT',
        constraint: {
            required: true
        },
        templatable: true
    })
    public content: string;

    @Metamodel.attribute({
        display: shellStringResources.Subject,
        association: 'SUBJECT',
        templatable: true
    })
    public subject?: string;

    @Metamodel.attribute({
        display: shellStringResources.PostIn,
        association: 'OPERATIONTYPE',
        constraint: {
            required: true
        },
        templatable: true
    })
    public operationType: PostInType = PostInType.SendMessageInChannel;

    private static validateConnection(_microsoftTeamsActivity?: MicrosoftTeamsActivity, linkedService?: LinkedService): IViolation[] | undefined {
        if (!linkedService) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.ConnectionLabel)
                }
            ];
        } else if (linkedService && linkedService.getLinkedServiceType() != Metamodel.modelFor(MicrosoftTeamsLinkedService).name) {
            return [
                {
                    message: () =>
                        format(
                            shellStringResources.ActivityNotSupportLinkedServiceError,
                            format(shellStringResources.ModelName_ActivityTemplate, shellStringResources.MicrosoftTeamsIdLabel),
                            format(shellStringResources.ModelName_ConnectionTemplate, Metamodel.modelOf(linkedService).name)
                        ),
                    messageType: 'unsupported'
                }
            ];
        }
        return undefined;
    }

    private static validateTeamId(_microsoftTeamsActivity?: MicrosoftTeamsActivity, teamId?: string): IViolation[] | undefined {
        if (_microsoftTeamsActivity.operationType === PostInType.SendMessageInChannel && !teamId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.Team)
                }
            ];
        }
        return undefined;
    }

    private static validateChannelId(_microsoftTeamsActivity?: MicrosoftTeamsActivity, channelId?: string): IViolation[] | undefined {
        if (_microsoftTeamsActivity.operationType === PostInType.SendMessageInChannel && !channelId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.Channel)
                }
            ];
        }
        return undefined;
    }

    private static validateChatId(_microsoftTeamsActivity?: MicrosoftTeamsActivity, chatId?: string): IViolation[] | undefined {
        if (_microsoftTeamsActivity.operationType === PostInType.SendMessageInGroupChat && !chatId) {
            return [
                {
                    message: () => format(shellStringResources.RequiredTemplate, shellStringResources.GroupChat)
                }
            ];
        }
        return undefined;
    }

    public onActivityCloned(clonedActivity: MicrosoftTeamsActivity, context: { inlineDataset?: boolean }): void {
        if (GlobalVariables.isTrident) {
            if (this.linkedService?.parameterized) {
                clonedActivity.linkedService = Copier.clone(
                    context,
                    [this.linkedService],
                    undefined,
                    true
                )[0] as MicrosoftTeamsLinkedService;
            }
        }
    }
}